import {
  Field,
  FieldArray,
  Form, Formik,
} from 'formik';
import React from 'react';
import { toast } from 'sonner';
import * as Yup from 'yup';

import { Checkbox, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';

import CustomSelect from 'components/CustomSelect/CustomSelect';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { ESaveButtonFormikStatus } from 'components/SaveButton/SaveButton';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import { updateUser } from 'utils/api';
import { phoneRegExp, showAPIErrorAlert } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  settingsContainer: {
    '& > div': {
      backgroundColor: '#F5F5F5',
      borderRadius: '12px',
      padding: '20px',
    },
    '& > div:first-child': {
      margin: '20px 0 0',
    },
    '& > div:not(:first-child)': {
      margin: '40px 0 0',
    },
  },
  name: {
    fontWeight: 'bold',
    fontSize: '16px',
    lineHeight: '24px',
    color: theme.palette.grayScale9.main,
  },
  description: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.lightGray.main,
    margin: '8px 0 20px',
  },
  checkboxesContainer: {
    display: 'flex',
    flexDirection: 'row',
    '& > div:not(:last-child)': {
      marginRight: '40px',
    },
  },
  checkbox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 500,
    textAlign: 'center',
    color: theme.palette.grayScale9.main,

    '& > span': {
      marginRight: '10px',
    },
  },
  select: {
    margin: '30px 0 0',
    maxWidth: '200px',
  },

  input: {
    width: '100%',
    maxWidth: 350,
  },
  alternatesChipsList: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: theme.spacing(0.5),
    margin: '24px 0 24px',
  },
  alternatesChip: {
    margin: theme.spacing(0.5),
    fontSize: '14px',
    fontWeight: 500,
  },
}));

const NotificationSettingsForm = ({ settings }) => {
  const classes = useStyles();
  const { userInfo } = useUserInfoContext();

  if (!userInfo) {
    return (<LoadingIndicator />);
  }

  return (
    <div>
      <Formik
        initialValues={{
          email: '',
          phoneNumber: '',
          notificationSettings: userInfo.notificationSettings,
          alternateNotificationEmails: userInfo.alternateNotificationEmails ?? [],
          alternateNotificationPhoneNumbers: userInfo.alternateNotificationPhoneNumbers ?? [],
        }}
        onSubmit={async (values, { setStatus }) => {
          const { notificationSettings, alternateNotificationEmails, alternateNotificationPhoneNumbers } = values;

          try {
            await updateUser({
              notificationSettings,
              alternateNotificationEmails,
              alternateNotificationPhoneNumbers,
            });

            toast.success('Your changes has been saved');
          } catch (err) {
            setStatus(ESaveButtonFormikStatus.Error);
            console.error('error', err);
            showAPIErrorAlert(err);
          }
        }}
        validationSchema={Yup.object().shape({
          notificationSettings: Yup.array().of(Yup.object().shape({
            setting: Yup.string().required(),
            selectedTypeOptions: Yup.array().of(Yup.string()).required(),
            selectedIntervalOption: Yup.string(),
          })),
          alternateNotificationEmails: Yup.array().of(Yup.string().email()).max(3),
          alternateNotificationPhoneNumbers: Yup.array().of(Yup.string().matches(phoneRegExp, 'Phone number is not valid')).max(3),
        })}
      >
        {({ values, setFieldValue, handleSubmit }) => {
          const onAlternateEmailKeyDown = async (e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab' || e.key === ';' || e.key === ' ') {
              e.preventDefault();

              const value = e.target.value.toLowerCase();
              try {
                // Validate email
                await Yup.string().email().validate(value);

                setFieldValue('email', '');

                // Ignore duplicates
                if (values.alternateNotificationEmails.includes(value)) return;

                // Max 3
                if (values.alternateNotificationEmails.length >= 3) {
                  toast.warning('You can only have up to three alternate emails!');
                  return;
                }

                setFieldValue('alternateNotificationEmails', [...values.alternateNotificationEmails, value]);
              } catch (err) {
                toast.warning('Please enter a valid email');
              }
            }
          };

          const onAlternatePhoneKeyDown = async (e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab' || e.key === ';' || e.key === ' ') {
              e.preventDefault();

              const { value } = e.target;
              try {
                // Validate email
                await Yup.string().matches(phoneRegExp).validate(value);

                setFieldValue('phoneNumber', '');

                // Ignore duplicates
                if (values.alternateNotificationPhoneNumbers.includes(value)) return;

                // Max 3
                if (values.alternateNotificationPhoneNumbers.length >= 3) {
                  toast.warning('You can only have up to three alternate phone numbers!');
                  return;
                }

                setFieldValue('alternateNotificationPhoneNumbers', [...values.alternateNotificationPhoneNumbers, value]);
              } catch (err) {
                toast.warning('Please enter a valid phone number');
              }
            }
          };

          const deleteAlternateEmailOrPhoneNumber = (s) => {
            // Simpler to remove from both, no value overlap technically
            setFieldValue('alternateNotificationEmails', values.alternateNotificationEmails.filter((s2) => s !== s2));
            setFieldValue('alternateNotificationPhoneNumbers', values.alternateNotificationPhoneNumbers.filter((s2) => s !== s2));
          };

          React.useEffect(() => {
            handleSubmit(); // Trigger form submission to auto-save
          }, [
            values.alternateNotificationEmails,
            values.alternateNotificationPhoneNumbers,
          ]);

          return (
            <Form>
              <div className={classes.settingsContainer}>
                <div>
                  <div className={classes.name}>Add Alternate Recipients for Your Notifications</div>
                  <p className={classes.description}>
                    We notify you and (if applicable) the second agent in your marketing profile when the following events occur.
                    <br />
                    To add an additional person to receive your notifications, enter their information below.
                  </p>

                  <ul className={classes.alternatesChipsList}>
                    {[...values.alternateNotificationEmails, ...values.alternateNotificationPhoneNumbers].map(((o, index) => (
                      <li key={index}>
                        <Chip
                          color="primary"
                          label={o}
                          onDelete={() => deleteAlternateEmailOrPhoneNumber(o)}
                          className={classes.alternatesChip}
                        />
                      </li>
                    )))}
                  </ul>

                  <Field
                    name="email"
                  >
                    {({ field, meta: { error } }) => (
                      <CustomInputDashboard
                        labelText="Add Email"
                        margin="normal"
                        inputProps={{
                          ...field,
                          placeholder: 'Type email and press Enter',
                          className: classes.input,
                          onKeyPress: onAlternateEmailKeyDown,
                        }}
                        error={error !== undefined}
                      />
                    )}
                  </Field>

                  <div style={{ marginTop: '10px' }} />

                  <Field
                    name="phoneNumber"
                  >
                    {({ field, meta: { error } }) => (
                      <CustomInputDashboard
                        labelText="Add Phone Number"
                        margin="normal"
                        inputProps={{
                          ...field,
                          placeholder: 'Type phone number and press Enter',
                          type: 'tel',
                          className: classes.input,
                          onKeyPress: onAlternatePhoneKeyDown,
                        }}
                        mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        error={error !== undefined}
                      />
                    )}
                  </Field>
                </div>

                <FieldArray
                  name="notificationSettings"
                  render={(arrayHelpers) => values.notificationSettings.map(({
                    setting, selectedTypeOptions, selectedIntervalOption,
                  }, index) => {
                    const notificationSetting = settings.filter(({ _id }) => _id === setting)[0];

                    const {
                      name, description, typeOptions, intervalOptions, modifiable,
                    } = notificationSetting;

                    const toggleTypeOption = (option) => {
                      const newSelectedTypeOptions = [...selectedTypeOptions];
                      if (newSelectedTypeOptions.includes(option)) {
                        newSelectedTypeOptions.splice(newSelectedTypeOptions.indexOf(option), 1);
                      } else {
                        newSelectedTypeOptions.push(option);
                      }

                      arrayHelpers.replace(index, {
                        setting,
                        selectedTypeOptions: newSelectedTypeOptions,
                        selectedIntervalOption,
                      });

                      handleSubmit(); // Trigger form submission to auto-save
                    };

                    const selectIntervalOption = (interval) => {
                      arrayHelpers.replace(index, {
                        setting,
                        selectedTypeOptions,
                        selectedIntervalOption: interval,
                      });

                      handleSubmit(); // Trigger form submission to auto-save
                    };

                    return (
                      <div key={index}>
                        <div className={classes.name}>{name}</div>
                        <p className={classes.description}>{description}</p>

                        <div className={classes.checkboxesContainer}>
                          {typeOptions.map((option) => (
                            <div key={option} className={classes.checkbox}>
                              <Checkbox
                                color="primary"
                                checked={selectedTypeOptions.includes(option)}
                                tabIndex={-1}
                                onClick={() => toggleTypeOption(option)}
                                disabled={!modifiable}
                              />
                              {option}
                            </div>
                          ))}
                        </div>

                        {/* Only show the interval options if one of the type options are selected */}
                        {selectedTypeOptions.length > 0 && intervalOptions && intervalOptions.length > 0 && (
                          <div className={classes.select}>
                            <CustomSelect
                              labelText="Interval"
                              labelBold
                              labelLight
                              items={intervalOptions.map((interval) => ({ value: interval, name: interval }))}
                              inputProps={{
                                value: selectedIntervalOption,
                                onChange: (e) => selectIntervalOption(e.target.value),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default NotificationSettingsForm;
